import { FormControl } from "@/components/ui/FormControl";
import classes from "./DonationSource.module.scss";
import { Radio, RadioGroup } from "@/components/ui/Radio";
import { Stack } from "@/components/ui/Stack";
import { GiftAidWithConfig } from "@/components/GiftAid";
import { Text } from "@/components/ui/Text";
import { Select } from "@/components/ui/Select";
import { Input } from "@/components/ui/Input";
import { isRecurringLike, useDonationFormStore } from "@/stores";
import { clsx } from "clsx";
import { useTranslation } from "@/hooks/useTranslation";
import type { SubOption } from "@/types/components/DonationSourceComponent";
import type { Option, ImperativeHandle } from "@/types/Form";
import type { DonationSourceProps } from "./DonationSourceProps";
import { useImperativeHandle, useLayoutEffect, useRef, useState } from "react";
import { ValidationGroupProvider } from "@/contexts/ValidationGroupProvider";
import { Field } from "@/components/Field";
import * as z from "zod";
import type { DonationFormStateValues } from "@/stores";

export function DonationSource(props: DonationSourceProps) {
  const { config, className, ref } = props;

  const { donationSource, setDonationSource, onBehalf, donationType } =
    useDonationFormStore();

  const t = useTranslation("donation_source");

  const giftAidRef = useRef<ImperativeHandle>(null);
  const donationSourceRef = useRef<ImperativeHandle>(null);

  const [showDonationSource, setShowDonationSource] = useState<boolean>(false);

  useImperativeHandle(ref, () => ({
    validate: async () => {
      const giftAidValidation = await giftAidRef.current?.validate();
      if (giftAidValidation && !giftAidValidation.isValid) {
        return giftAidValidation;
      }

      if (!donationSourceRef.current) {
        return { isValid: true };
      }

      return donationSourceRef.current.validate();
    },
  }));

  useLayoutEffect(() => {
    if (isRecurringLike(donationType)) {
      setDonationSource("own-money");
    } else {
      setShowDonationSource(true);
    }
  }, [donationType, setDonationSource]);

  const getFormattedSubOptions = (subOptions: SubOption[]): Option[] => {
    if (subOptions.length === 0) return [];

    const empty = { value: "", label: t("sub-option-empty-label") as string };
    const formattedSubOptions = subOptions.map((option) => ({
      value: option.value,
      label: t(option["sub-option-label"].i18n) as string,
    }));

    return [empty, ...formattedSubOptions];
  };

  const options = config["donation-source-options"];
  const selectedOption = options.find(
    (option) => option.value === donationSource,
  );
  const subOptions = selectedOption ? selectedOption["sub-options"] : [];
  const subOptionsFormatted = getFormattedSubOptions(subOptions);

  const hasSubOptions = subOptions.length > 0;
  const selectedSubOption = subOptions.find(
    (option) => option.value === onBehalf,
  );

  const notice = selectedSubOption ? selectedSubOption["policy-notice"] : null;

  const policyCompanyLinkI18n = selectedSubOption
    ? selectedSubOption["policy-link-url"]?.i18n
    : null;

  const policyCompanyLinkUrl = policyCompanyLinkI18n
    ? (t(policyCompanyLinkI18n) as string)
    : null;

  const additionalInformation = t(
    config["donation-source-additional-information"].i18n,
  );

  const values = Object.values(options).map((option) => option.value) as [
    string,
  ];

  return (
    <ValidationGroupProvider ref={donationSourceRef}>
      <div className={clsx(classes.wrapper, className)}>
        <Stack gap="md">
          {showDonationSource && (
            <Stack gap="sm">
              <Field<DonationFormStateValues>
                name="donationSource"
                serverErrorPaths={["donation_source.donation_source"]}
                validate={() => {
                  return z.enum(values, {
                    errorMap: () => ({
                      message: t("errors.donation-source-required") as string,
                    }),
                  });
                }}
                render={({
                  value,
                  handleChange,
                  errorMessage,
                  handleBlur,
                  ref,
                }) => (
                  <FormControl
                    type="fieldset"
                    label={t(config["donation-source-main-question"].i18n)}
                    error={errorMessage}
                    required
                  >
                    <RadioGroup
                      name="donation-source"
                      value={value}
                      direction="column"
                      onChange={(ev) => handleChange(ev.target.value)}
                    >
                      {options.map((option, index) => (
                        <Radio
                          onBlur={() => handleBlur()}
                          key={option.value}
                          value={option.value}
                          // The group is validated as a unit, so the validation
                          // layer focuses and scrolls to its first option.
                          ref={
                            index === 0
                              ? (ref as React.RefObject<HTMLInputElement>)
                              : undefined
                          }
                        >
                          {t(option.label.i18n)}
                        </Radio>
                      ))}
                    </RadioGroup>
                  </FormControl>
                )}
              />

              {additionalInformation && (
                <Text size="sm" italic>
                  {additionalInformation}
                </Text>
              )}
            </Stack>
          )}

          {donationSource === "own-money" && (
            <GiftAidWithConfig ref={giftAidRef} />
          )}

          {hasSubOptions && (
            <Stack className={classes.spaceTop} gap="md">
              <Stack gap="sm">
                <Field<DonationFormStateValues>
                  name="onBehalf"
                  validate={() => {
                    return z.string().min(1, {
                      message: t("errors.on-behalf-required") as string,
                    });
                  }}
                  render={({
                    value,
                    handleChange,
                    handleBlur,
                    ref,
                    errorMessage,
                    isValid,
                  }) => (
                    <FormControl
                      label={t("on-behalf-of")}
                      error={errorMessage}
                      isValid={isValid}
                      required
                    >
                      <Select
                        ref={ref as React.RefObject<HTMLSelectElement>}
                        name="onBehalf"
                        value={value}
                        onChange={(ev) => handleChange(ev.target.value)}
                        onBlur={() => handleBlur()}
                      >
                        {subOptionsFormatted.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />

                {notice && (
                  <Text
                    className={classes.notice}
                    size="sm"
                    italic
                    gutterBottom
                  >
                    {t(notice.i18n, {
                      policy_company_link_url: policyCompanyLinkUrl ?? "",
                    })}
                  </Text>
                )}
              </Stack>

              <Field<DonationFormStateValues>
                name="organisationName"
                validate={() => {
                  return z.string().min(1, {
                    message: t("errors.organisation-name-required") as string,
                  });
                }}
                render={({
                  value,
                  handleChange,
                  handleBlur,
                  ref,
                  errorMessage,
                  isValid,
                }) => (
                  <FormControl
                    label={t("organisation-name")}
                    error={errorMessage}
                    isValid={isValid}
                    required
                  >
                    <Input
                      ref={ref as React.RefObject<HTMLInputElement>}
                      value={value}
                      onChange={(e) => handleChange(e.target.value)}
                      onBlur={() => handleBlur()}
                      name="organisationName"
                    />
                  </FormControl>
                )}
              />
            </Stack>
          )}
        </Stack>
      </div>
    </ValidationGroupProvider>
  );
}
